
import { useCallback } from "react";
import { Resource, PermissionType } from "../types";
import { checkPermission } from "../permissionChecks";

/**
 * Hook for checking if a role has permissions for a specific resource and action
 */
export const usePermissionChecker = ({
  user,
  permissions,
  isLoading,
  initialized
}) => {
  
  // Check if a user has permission for a resource and action
  const hasPermission = useCallback((resource: Resource, permissionType: PermissionType = "read"): boolean => {
    if (!user) {
      console.log(`[useRolePermissions] No user found, denying access to ${resource}`);
      return false;
    }
    
    // Management role ALWAYS has access to everything - highest priority check
    if (user.role === "management") {
      console.log(`[useRolePermissions] Management role automatically has ${permissionType} permission for ${resource}`);
      return true;
    }
    
    // Special case for feedback - all roles have access
    if (resource === "feedback" && (permissionType === "read" || permissionType === "write")) {
      console.log(`[useRolePermissions] All roles have ${permissionType} access to feedback`);
      return true;
    }
    
    // While loading, deny access
    if (isLoading || !initialized.current) {
      console.log(`[useRolePermissions] Permissions still loading, denying access to ${resource} for ${user.role}`);
      return false;
    }
    
    // Perform the permission check
    const result = checkPermission(permissions, user.role, resource, permissionType);
    console.log(`[useRolePermissions] Permission check for ${user.role} - ${resource}.${permissionType}: ${result}`);
    return result;
  }, [user, permissions, isLoading, initialized]);

  return {
    hasPermission
  };
};
